import debounce from 'debounce';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ownerDocument } from '../../lib';
import ClModal from '../ClModal';
import ClPaper from '../ClPaper';
import { TransitionProps } from '../types';
import { TransformOrigin } from './ClPopover.types';

interface Rectangle {
  height: number;
  width: number;
}

interface OffsetPosition {
  top: number;
  left: number;
}

interface PositioningStyle {
  top?: string;
  left?: string;
  transformOrigin: string;
}

// Sum the scrollTop between two elements.
function getScrollParent(parent: Element, child: Element) {
  let element: Element = child;
  let scrollTop: number = 0;

  while (element && element !== parent) {
    element = element.parentNode as Element;
    scrollTop += element.scrollTop;
  }

  return scrollTop;
}

function getOffsetTop(rect: Rectangle, vertical: TransformOrigin['vertical']) {
  let offset = 0;

  if (typeof vertical === 'number') {
    offset = vertical;
  } else if (vertical === 'center') {
    offset = rect.height / 2;
  } else if (vertical === 'bottom') {
    offset = rect.height;
  }

  return offset;
}

function getOffsetLeft(
  rect: Rectangle,
  horizontal: TransformOrigin['horizontal']
) {
  let offset = 0;

  if (typeof horizontal === 'number') {
    offset = horizontal;
  } else if (horizontal === 'center') {
    offset = rect.width / 2;
  } else if (horizontal === 'right') {
    offset = rect.width;
  }

  return offset;
}

function getTransformOriginValue({
  horizontal,
  vertical
}: TransformOrigin): string {
  return [horizontal, vertical]
    .map(n => {
      return typeof n === 'number' ? `${n}px` : n;
    })
    .join(' ');
}

function ownerWindow(node: Element, fallback = window) {
  const doc = ownerDocument(node);
  return doc.defaultView || fallback;
}

const sleep = (milliseconds = 500) =>
  new Promise(resolve => setTimeout(resolve, milliseconds));

@Component({
  components: {
    ClModal,
    ClPaper
  }
})
export default class ClPopover extends ClModal {
  get menuElement(): Element {
    const menuRef = this.$refs.menu as Vue;
    return menuRef.$el;
  }
  /**
   * This is the element that may be used to set the position of the popover.
   */
  @Prop({
    type: [Object as () => Vue, Element]
  })
  public anchorEl!: Vue | Element;

  /**
   * This is the point on the anchor where the popover's
   * `anchorEl` will attach to. This is not used when the
   * anchorReference is 'anchorPosition'.
   *
   * Options:
   * vertical: [top, center, bottom];
   * horizontal: [left, center, right].
   */
  @Prop({
    type: Object as () => TransformOrigin,
    default: () => ({
      vertical: 'top',
      horizontal: 'left'
    })
  })
  public anchorOrigin!: TransformOrigin;

  @Prop({
    type: Boolean,
    default: false
  })
  public disableAutoFocusItem!: boolean;

  /**
   * If `true`, the modal will be rendered apart from the portal.
   * This is useful when your current component doesnt have a cl-provider.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public disablePortal!: boolean;

  /**
   * Props for cl-modal element.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public clModalProps!: object;

  /**
   * Props for cl-paper element.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public clPaperProps!: object;

  /**
   * The elevation of the popover.
   */
  @Prop({
    type: Number,
    default: 8
  })
  public elevation!: number;

  /**
   * Specifies how close to the edge of the window the popover can appear.
   */
  @Prop({
    type: Number,
    default: 16
  })
  public marginThreshold!: number;

  /**
   * If true, the popover will be visible.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public open!: boolean;

  @Prop({
    type: Object as () => TransformOrigin,
    default: () => ({
      vertical: 'top',
      horizontal: 'left'
    })
  })
  public transformOrigin!: TransformOrigin;

  @Prop({
    type: String,
    default: '0.1s'
  })
  public transitionDuration!: string;

  @Prop({
    type: Object as () => TransitionProps,
    default: () => ({
      name: 'zoom'
    })
  })
  public transitionProps!: TransitionProps;

  public handleResize = debounce(() => {
    this.$nextTick(() => {
      const el = this.getElement(this.$refs.popover as Vue | Element);
      if (!el) {
        return;
      }
      this.setPositioningStyles(el);
    });
  }, 166); // Corresponds to 10 frames at 60 Hz.

  private popoverStyles: object = {};

  public setPositioningStyles(element: Element) {
    this.popoverStyles = Object.assign(
      {},
      this.popoverStyles,
      this.getPositioningStyle(element),
      {
        animationDuration: this.transitionDuration
      }
    );
  }

  public setVisibility(visibility = 'visible') {
    this.popoverStyles = Object.assign({}, this.popoverStyles, {
      visibility
    });
  }

  public getTransformOrigin(elemRect: Rectangle, contentAnchorOffset: number) {
    return {
      vertical:
        getOffsetTop(elemRect, this.transformOrigin.vertical) +
        contentAnchorOffset,
      horizontal: getOffsetLeft(elemRect, this.transformOrigin.horizontal)
    };
  }

  // Returns the vertical offset of inner content to anchor the transform on if provided
  public getContentAnchorOffset(element: Element): number {
    const contentAnchorOffset = 0;
    return contentAnchorOffset;
  }

  getElement(el: Vue | Element): Element {
    return Boolean(el && (el as Vue).$el) ? (el as Vue).$el : (el as Element);
  }

  get computedAnchorEl(): Element {
    if (!this.anchorEl) {
      // return body if there's not an anchor element
      const popover = this.getElement(this.$refs.popover as Vue);
      return ownerDocument(popover).body;
    }
    return this.getElement(this.anchorEl);
  }

  // Returns the top/left offset of the position
  // to attach to on the anchor element (or body if none is provided)
  public getAnchorOffset(contentAnchorOffset: number): OffsetPosition {
    if (
      !this.computedAnchorEl ||
      !this.computedAnchorEl.getBoundingClientRect
    ) {
      return {
        top: 0,
        left: 0
      };
    }

    // If an anchor element wasn't provided, just use the parent body element
    const anchorRect = this.computedAnchorEl.getBoundingClientRect();
    const anchorVertical =
      contentAnchorOffset === 0 ? this.anchorOrigin.vertical : 'center';

    return {
      top: anchorRect.top + getOffsetTop(anchorRect, anchorVertical),
      left:
        anchorRect.left +
        getOffsetLeft(anchorRect, this.anchorOrigin.horizontal)
    };
  }

  public getPositioningStyle(element: Element): PositioningStyle {
    if (!element) {
      return {
        top: `0px`,
        left: `0px`,
        transformOrigin: ''
      };
    }

    const contentAnchorOffset = this.getContentAnchorOffset(element);
    const elemRect = {
      width: (element as HTMLElement).offsetWidth,
      height: (element as HTMLElement).offsetHeight
    };
    // Get the transform origin point on the element itself
    const transformOrigin = this.getTransformOrigin(
      elemRect,
      contentAnchorOffset
    );

    // Get the offset of of the anchoring element
    const anchorOffset = this.getAnchorOffset(contentAnchorOffset);
    let top = anchorOffset.top - transformOrigin.vertical;
    let left = anchorOffset.left - transformOrigin.horizontal;
    const bottom = top + elemRect.height;
    const right = left + elemRect.width;

    // Use the parent window of the anchorEl if provided
    const containerWindow = ownerWindow(this.computedAnchorEl);

    // Window thresholds taking required margin into account
    const heightThreshold = containerWindow.innerHeight - this.marginThreshold;
    const widthThreshold = containerWindow.innerWidth - this.marginThreshold;

    // Check if the vertical axis needs shifting
    if (top < this.marginThreshold) {
      const diff = top - this.marginThreshold;
      top -= diff;
      transformOrigin.vertical += diff;
    } else if (bottom > heightThreshold) {
      const diff = bottom - heightThreshold;
      top -= diff;
      transformOrigin.vertical += diff;
    }

    // Check if the horizontal axis needs shifting
    if (left < this.marginThreshold) {
      const diff = left - this.marginThreshold;
      left -= diff;
      transformOrigin.horizontal += diff;
    } else if (right > widthThreshold) {
      const diff = right - widthThreshold;
      left -= diff;
      transformOrigin.horizontal += diff;
    }

    return {
      top: `${top}px`,
      left: `${left}px`,
      transformOrigin: getTransformOriginValue(transformOrigin)
    };
  }

  @Watch('open')
  public async handleOpen(open: boolean) {
    if (!open) {
      this.setVisibility('hidden');
      return;
    }
    await sleep(1);
    const el = this.getElement(this.$refs.popover as Vue | Element);
    this.setPositioningStyles(el);
    await sleep(1);
    this.setVisibility();
    this.$emit('enter', el);
  }

  public beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.handleResize.clear();
  }

  public mounted() {
    window.addEventListener('resize', this.handleResize);
    if (this.open) {
      this.handleOpen(true);
    }
  }
}
